import { create } from "zustand";
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit
} from "firebase/firestore";
import { db, firestore } from "../lib/firebase";
import { useAuthStore } from "./auth.store";
import { onValue, ref } from "firebase/database";

interface RsiReading {
  rsi: number;
  timestamp: number;
}

interface PriceReading {
  price: number;
  timestamp: number;
}

interface SymbolData {
  rsiReadings: RsiReading[];
  priceReadings: PriceReading[];
  rsiTrend: "up" | "down" | "neutral";
}

interface IndicatorsState {
  indicators: any | null;
  signals: any | null;
  loading: boolean;
  processedData: { [key: string]: SymbolData };
  getDataForSymbol: (symbol: string) => SymbolData;
  fetchIndicatorReadingsAndProcessData: (
    symbol: string,
    tickerInterval: string
  ) => Promise<void>;
}

// Helper functions

// function resampleData(data: any, segmentSize: number) {
//   const resampled = [];
//   for (let i = 0; i < data.length; i += segmentSize) {
//     const segment = data.slice(i, i + segmentSize);
//     const average =
//       segment.reduce((acc: number, val: number) => acc + val, 0) /
//       segment.length;
//     resampled.push(average);
//   }
//   return resampled;
// }

export const transformTimestamp = (timestamp: number) => {
  const date = new Date(timestamp * 1000);

  const formattedDate = date
    .toISOString()
    .replace("T", " ")
    .replace(/\.\d+Z$/, "");

  return formattedDate;
};

const calculateRsiTrend = (
  rsiReadings: RsiReading[]
): "up" | "down" | "neutral" => {
  if (!rsiReadings.length) return "neutral";

  rsiReadings.sort((a, b) => a.timestamp - b.timestamp);

  let trend: "up" | "down" | "neutral" = "neutral";
  const total = rsiReadings.reduce((acc, curr, index) => {
    if (index === 0) return acc;
    return acc + (curr.rsi - rsiReadings[index - 1].rsi);
  }, 0);

  if (total > 0) trend = "up";
  else if (total < 0) trend = "down";

  return trend;
};

export const useIndicatorsStore = create<IndicatorsState>((set, get) => ({
  indicators: null,
  signals: null,
  loading: false,
  processedData: {},
  getDataForSymbol: (symbol: string) => {
    return get().processedData[symbol];
  },
  fetchIndicatorReadingsAndProcessData: async (
    symbol: string,
    tickerInterval: string
  ) => {
    try {
      const indicatorKey = `is_${tickerInterval}`;
      const historyRef = collection(
        firestore,
        `/indicators_24h/${symbol}/history`
      );
      const q = query(
        historyRef,
        where(indicatorKey, "==", true),
        orderBy("timestamp", "desc"),
        limit(60)
      );

      const snapshot = await getDocs(q);
      const rsiReadings: RsiReading[] = [];
      const priceReadings: PriceReading[] = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        rsiReadings.push({
          rsi: data.rsi[tickerInterval]["14"],
          timestamp: data.timestamp
        });
        priceReadings.push({
          price: data.ticker.close,
          timestamp: data.timestamp
        });
      });

      // Get the latest rsi reading from indicators with symbol and interval
      const indicatorReading = get().indicators?.[symbol];
      if (indicatorReading) {
        const rsi = indicatorReading?.rsi[tickerInterval || "15m"]["14"];
        if (rsi) {
          rsiReadings.push({
            rsi,
            timestamp: indicatorReading.timestamp
          });
        }
      }

      const rsiTrend = calculateRsiTrend(rsiReadings);
      // Update state with new data
      set(state => ({
        processedData: {
          ...state.processedData,
          [symbol]: {
            rsiReadings,
            priceReadings,
            rsiTrend
          }
        }
      }));
    } catch (error) {
      console.error("Failed to fetch indicator readings:", error);
    }
  }
}));

useAuthStore.subscribe(authState => {
  if (!authState.user) return;
  const indicatorsRef = ref(db, `indicators/`);
  onValue(indicatorsRef, snapshot => {
    useIndicatorsStore.setState({ loading: true });
    const data = snapshot.val();
    if (data) {
      useIndicatorsStore.setState({ indicators: data, loading: false });
    }
  });

  const signalsRef = ref(db, "signals/");
  onValue(signalsRef, snapshot => {
    console.log("Signals updated");
    const data = snapshot.val();
    if (data) {
      useIndicatorsStore.setState({ signals: data, loading: false });
    }
  });
});

// const connectedRef = ref(db, ".info/connected");
// onValue(connectedRef, snap => {
//   if (snap.val() === true) {
//     // Connected or reconnected. Ensure listeners are active here.
//     console.log("Connected / Reconnected");
//   } else {
//     // Not connected. Handle accordingly.
//     console.log("Disconnceted");
//   }
// });
